import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Loader2, Shuffle } from "lucide-react";

const PRESETS = [
  { label: "3 min",  seconds: 180 },
  { label: "5 min",  seconds: 300 },
  { label: "10 min", seconds: 600 },
];

export default function BattleModal({ open, onOpenChange, myStreamId, onCreated }) {
  const [duration, setDuration] = useState(300);
  const [custom, setCustom] = useState("");
  const [opponents, setOpponents] = useState([]); // other live streams
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setCustom("");
    setDuration(300);
    (async () => {
      try {
        // Phase 4B — dedicated opponent endpoint; bypasses public-feed side filter.
        const { data } = await api.get("/battles/available-opponents");
        setOpponents(data?.opponents || []);
      } catch (e) { setOpponents([]); }
    })();
  }, [open, myStreamId]);

  const useCustom = () => {
    const n = parseInt(custom, 10);
    if (!n || n < 1 || n > 30) {
      toast.error("Enter minutes between 1 and 30.");
      return;
    }
    setDuration(n * 60);
  };

  const startBattle = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await api.post("/battles", {
        opponent_stream_id: selected.id,
        duration_seconds: duration,
      });
      toast.success(`Challenge sent to ${selected.host?.display_name}`);
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start battle.");
    } finally {
      setBusy(false);
    }
  };

  const startRandomBattle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/battles/random", { duration_seconds: duration });
      toast.success("Random challenge fired — waiting for opponent to accept.");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No random opponents available.");
    } finally {
      setBusy(false);
    }
  };

  const mins = Math.round(duration / 60);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-md border-white/10 max-h-[85vh] overflow-y-auto" data-testid="battle-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Swords size={18} className="text-rose-500" /> Start a battle
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            Challenge another live streamer. Winner: most coins in the timer window.
          </DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Duration ({mins} min)</div>
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.seconds}
                type="button"
                onClick={() => { setDuration(p.seconds); setCustom(""); }}
                data-testid={`battle-preset-${p.seconds}`}
                className={`py-2 text-xs uppercase tracking-widest border transition-colors ${
                  duration === p.seconds && !custom
                    ? "bg-rose-600/20 border-rose-500 text-white"
                    : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              value={custom}
              onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
              placeholder="Custom minutes (1-30)"
              className="bg-black/40 border-white/10 rounded-none py-5"
              data-testid="battle-custom-minutes"
            />
            <Button onClick={useCustom} variant="outline" className="rounded-none border-white/15">Set</Button>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Pick an opponent</div>
          <button
            type="button"
            onClick={startRandomBattle}
            disabled={busy}
            data-testid="battle-random-button"
            className="w-full mb-2 flex items-center justify-center gap-2 bg-gradient-to-r from-rose-600 to-fuchsia-600 hover:brightness-110 disabled:opacity-50 text-white font-semibold rounded-none py-3 text-sm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Shuffle size={14} />}
            Random opponent (fastest match)
          </button>
          <div className="space-y-2" data-testid="battle-opponents">
            {opponents.length === 0 && (
              <div className="text-xs text-zinc-500 text-center py-4">
                No other live streamers to battle right now.
              </div>
            )}
            {opponents.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelected(s)}
                data-testid={`battle-opponent-${s.host?.id}`}
                className={`w-full flex items-center gap-3 border px-3 py-2 text-left transition-colors ${
                  selected?.id === s.id
                    ? "bg-rose-600/20 border-rose-500"
                    : "bg-black/30 border-white/10 hover:border-white/30"
                }`}
              >
                <Avatar className="w-9 h-9 border border-white/10">
                  <AvatarImage src={s.host?.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-xs">
                    {s.host?.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.host?.display_name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{s.title || "Untitled Live"}</div>
                </div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{s.viewer_count || 0} viewers</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={startBattle}
            disabled={!selected || busy}
            className="btn-crimson rounded-none px-5 py-5"
            data-testid="battle-start-button"
          >
            {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Sending challenge…</> : "Send challenge"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
